import { useEffect, useMemo, useState } from 'react'

import AutowidthInput from 'react-autowidth-input'
import { CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Skeleton,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'

import { generateTeamName } from '@/helpers/team-name/team-name'
import { useUser } from '@/react-query/user'
import {
  useSendTestOptoutEmail,
  useUpdateUserSettings,
  useUserSettings,
} from '@/react-query/user-settings'
import { PencilSvg } from '@/views/assets/PencilSvg'
import Card from '@/views/components/Card/Card'

export const Consent = () => {
  return (
    <Card w={'100%'} p={6} px={10}>
      <Heading size={'md'}>Consent</Heading>
      <VStack mt={6} alignItems={'flex-start'} gap={4} w={'100%'}>
        <Optout />
      </VStack>
    </Card>
  )
}

const Optout = () => {
  return (
    <VStack alignItems={'flex-start'} gap={4} w={'100%'}>
      <EnableRecordingOptout />
      <OptoutCompanyName />
    </VStack>
  )
}

const EnableRecordingOptout: React.FC = () => {
  const userSettings = useUserSettings()
  const updateUserSettings = useUpdateUserSettings()

  const user = useUser()
  const defaultCompanyName = generateTeamName(
    user.data?.email || '',
    user.data?.displayName || '',
    'company',
  )

  return (
    <HStack w="100%" wrap={'wrap'} spacing={8} rowGap={4}>
      <FormControl display={'inline-flex'} alignItems={'center'} w={'auto'}>
        <Skeleton
          rounded={'1.25rem'}
          isLoaded={!userSettings.isLoading}
          startColor="#f5f5f5"
          endColor="#d7d7d7"
        >
          <Switch
            id="enable-recording-optout"
            isChecked={userSettings.data?.enableCandidateRecordingOptout}
            onChange={(event) => {
              event.preventDefault()
              updateUserSettings.mutate({
                enableCandidateRecordingOptout:
                  !userSettings.data?.enableCandidateRecordingOptout,
                recordingOptoutCompanyName:
                  userSettings.data?.recordingOptoutCompanyName ||
                  defaultCompanyName,
              })
            }}
            size={'lg'}
            variant={'brand-black'}
            isDisabled={userSettings.isLoading}
          />
        </Skeleton>
        <FormLabel htmlFor="enable-recording-optout" mb="0" ml={'4'}>
          Email all candidates allowing to decline transcription
        </FormLabel>
      </FormControl>
      <SendTestEmailButton />
    </HStack>
  )
}

const SendTestEmailButton: React.FC = () => {
  const [sent, setSent] = useState(false)

  useEffect(() => {
    if (sent) {
      setTimeout(() => setSent(false), 1000 * 10)
    }
  }, [sent])

  const sendTestOptoutEmail = useSendTestOptoutEmail()

  return (
    <Button
      minW={'200px'}
      variant={'brand-solid-white'}
      isDisabled={sent}
      onClick={async () => {
        await sendTestOptoutEmail.mutateAsync()
        setSent(true)
      }}
      isLoading={sendTestOptoutEmail.isPending}
      leftIcon={sent ? <CheckIcon /> : undefined}
    >
      {sent ? 'Test email sent' : 'Send me a test email'}
    </Button>
  )
}

const OptoutCompanyName: React.FC = () => {
  const user = useUser()
  const userSettings = useUserSettings()
  const updateUserSettings = useUpdateUserSettings()

  const defaultCompanyName = useMemo(() => {
    return generateTeamName(
      user.data?.email || '',
      user.data?.displayName || '',
      'company',
    )
  }, [user.data?.email, user.data?.displayName])

  const [localCompanyName, setLocalCompanyName] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!isEditing) {
      setLocalCompanyName(
        userSettings?.data?.recordingOptoutCompanyName || defaultCompanyName,
      )
    }
  }, [
    isEditing,
    userSettings?.data?.recordingOptoutCompanyName,
    defaultCompanyName,
  ])

  return (
    <HStack>
      <Text>Company name in emails: </Text>
      <Skeleton
        rounded={'1.25rem'}
        isLoaded={!userSettings.isLoading}
        startColor="#f5f5f5"
        endColor="#d7d7d7"
      >
        <Box fontWeight={'bold'}>
          {isEditing ? (
            <Input
              minW={'100px'}
              pr={0}
              value={localCompanyName}
              onChange={(e) => setLocalCompanyName(e.target.value)}
              variant={'brand-solid-white'}
              as={AutowidthInput}
            />
          ) : (
            <Text pr={4}>
              {userSettings?.data?.recordingOptoutCompanyName ||
                defaultCompanyName}
            </Text>
          )}
        </Box>
      </Skeleton>
      <IconButton
        onClick={async (e) => {
          e.preventDefault()
          if (isEditing) {
            // Save the updated company name
            updateUserSettings.mutate({
              recordingOptoutCompanyName: localCompanyName || '',
            })
          }
          setIsEditing(!isEditing)
        }}
        variant="brand-solid-white"
        icon={isEditing ? <CheckIcon /> : <PencilSvg />}
        aria-label="Edit company name"
      />
    </HStack>
  )
}
